import { apiClient } from '@/services/api';
import type { DealMarketingCopy } from '@/types/deal';

export interface ReferralStatsLink {
  id: number;
  kind: 'generic' | 'person' | 'group';
  label: string;
  default_channel: string | null;
  clicks: number;
  conversions: number;
  paid: number;
}

/** Which glyph a proof tile draws. The server sends the enum; ProofTiles maps it. */
export type ProofIcon = 'people' | 'speed';

/**
 * The Refer tab's marketing block — tagline, athlete story card, proof
 * tiles — authored in the backend's `Api::ReferIndexCopy` and rendered here
 * verbatim, like `Deal.marketing_copy`. Nothing in it is written in the app.
 */
export interface ReferMarketing {
  tagline: string;
  story: {
    headline: string;
    cta: string;
    /** Spoken for the card: the poster's words are pixels. */
    accessibility_label: string;
    /** Null makes the card a plain image (no button role, taps do nothing). */
    video_url: string | null;
    /** Null draws the headline and CTA as text on the card instead. */
    poster_url: string | null;
  };
  proof_points: { icon: ProofIcon | (string & {}); text: string }[];
}

export interface ReferralStatsResponse {
  totals: {
    clicks: number;
    conversions: number;
    /** Referrals that have reached the paid stage. */
    paid_referrals_count: number;
    earnings_cents: number;
    /**
     * Friends with a provider-confirmed step and nothing paid: the ones
     * "one step away". Powers the Started tab panel. (Until 2026-09 this
     * counted every unpaid referral; the server also sends a
     * `not_started_count` the app does not read, the feed's own
     * `chip_counts.not_started` covers that tab.)
     */
    friends_in_progress_count: number;
    /**
     * v3: friends_in_progress_count × the primary deal's referrer_cut_cents.
     * Powers the callout's subtitle. Server-computed so client doesn't
     * recompute the multiplication against a per-deal payout.
     */
    left_on_table_cents: number;
  };
  /**
   * App-level invite program terms, published by the server so the client
   * renders what we actually pay. `invitee_cents` is 0 while the program is
   * referrer-only — treat 0 as "promise the invitee nothing", which is what
   * lets that half be switched on server-side with no app release.
   */
  /**
   * Present only once the invite-program backend ships. Every reader treats
   * its absence as "no invite to offer" — the index draws no card and the
   * invite screen shows its unavailable state.
   */
  invite_program?: {
    referrer_cents: number;
    invitee_cents: number;
    /**
     * Same shape as `Deal.marketing_copy` — the invite is described exactly
     * the way a referable deal is, so both render through one path. No
     * invite copy lives in the app.
     */
    marketing_copy: DealMarketingCopy;
  };
  /**
   * Present once the backend serves it (PR #167). Absent → the index draws
   * no tagline, story or tiles: a blank is the truth, canned copy is not.
   */
  marketing?: ReferMarketing;
  per_link: ReferralStatsLink[];
  scope: {
    referral_link_id: number | null;
  };
}

export const referralStatsService = {
  /**
   * GET /api/v1/referrals/stats
   * Pass `referralLinkId` to scope to one of current_user's links, or
   * `dealId` to scope money and funnel numbers to one deal (the deal's
   * referral screen); omit both to aggregate across everything.
   */
  async fetch(scope?: number | { referralLinkId?: number; dealId?: number }): Promise<ReferralStatsResponse> {
    const opts = typeof scope === 'number' ? { referralLinkId: scope } : scope ?? {};
    const params = new URLSearchParams();
    if (opts.referralLinkId) params.set('referral_link_id', String(opts.referralLinkId));
    if (opts.dealId) params.set('deal_id', String(opts.dealId));
    const query = params.toString();
    return apiClient.get<ReferralStatsResponse>(`/api/v1/referrals/stats${query ? `?${query}` : ''}`);
  },
};
